import {
  DefectLegend,
  GetStructureDefectsDocument,
  TemplateFieldTypeEnum,
  useUpdateStructureMutation,
} from '@/__generated__/graphql';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { useHavePermission } from '@/hooks';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { Permissions } from '@/types';
import { useSnackBarMessage } from '../utils';
import { Delete, Edit } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import Button from '../../shared/Button';
import { structureDefectLegends } from '../state';
import { TemplateFields } from './shared';
import { Field, FieldPanel, FieldsList, FormHeader, LineBreak, tooltipStyles } from './styles';
import { PageLoader } from '../../shared/PageLoader';

export const DefectLegendTemplateForm = ({ structureId }: { structureId: string }) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [selectedDefectLegend, setSelectedDefectLegend] = useState<DefectLegend | undefined>(
    undefined
  );
  const [defectLegends, setDefectLegends] = useAtom(structureDefectLegends);
  const [isDeleteDefectDialogOpen, setIsDeleteDefectDialogOpen] = useState<boolean>(false);
  const canManageDefects = useHavePermission(Permissions.MANAGE_DEFECTS);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const updateField = (field: DefectLegend | undefined) => {
    if (!field) {
      return;
    }
    setSelectedDefectLegend(field);
    setDefectLegends((previous) => {
      if (!previous) return;
      return previous.map((item) => {
        if (item.id === field.id) {
          return field;
        }
        return item;
      });
    });
  };

  const [updateStructure] = useUpdateStructureMutation({
    refetchQueries: [GetStructureDefectsDocument],
  });

  const handleSaveTemplate = useCallback(() => {
    const updated = defectLegends?.map((defect) => {
      return JSON.parse(JSON.stringify(defect), (key, value) => {
        return key === '__typename' ? undefined : value;
      });
    });
    updateStructure({
      variables: {
        input: {
          structureId,
          defectLegends: updated,
          lastModifiedFor: 'Update Defects Legend',
        },
      },
      onCompleted: (response) => {
        if (response?.updateStructure) {
          showSnackBar('Defects Legend updated successfully', 'success');
          setDefectLegends(response?.updateStructure.defectLegends);
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`Failed to update defect legend - ${errorResponse.message}`, 'error');
      },
    });
  }, [defectLegends, updateStructure, structureId, showSnackBar, setDefectLegends]);

  const handleDeleteDefectLegend = useCallback(() => {
    setIsDeleteDefectDialogOpen(true);
  }, []);

  const deleteDefectLegend = useCallback(() => {
    const updated = defectLegends
      ?.filter((defect) => defect.id !== selectedDefectLegend?.id)
      ?.map((defect) => {
        return JSON.parse(JSON.stringify(defect), (key, value) => {
          return key === '__typename' ? undefined : value;
        });
      });

    updateStructure({
      variables: {
        input: {
          structureId,
          defectLegends: updated,
          lastModifiedFor: 'Remove Defects Legend',
        },
      },
      onCompleted: (response) => {
        if (response?.updateStructure) {
          setSelectedDefectLegend(undefined);
          setDefectLegends(response?.updateStructure.defectLegends);
          setMode('ReadOnly');
          showSnackBar('Defects Legend removed successfully', 'success');
        }
      },
      onError: (errorResponse) => {
        console.error(errorResponse);
        showSnackBar(`Failed to remove defect legend - ${errorResponse.message}`, 'error');
      },
    });
    setIsDeleteDefectDialogOpen(false);
  }, [
    defectLegends,
    updateStructure,
    structureId,
    selectedDefectLegend?.id,
    setDefectLegends,
    showSnackBar,
  ]);

  if (!defectLegends) return <PageLoader />;

  return (
    <>
      {isDeleteDefectDialogOpen && (
        <GenericDialog
          open={isDeleteDefectDialogOpen}
          onClose={() => setIsDeleteDefectDialogOpen(false)}
          title="Delete Defect Legend"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this defect legend?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setIsDeleteDefectDialogOpen(false)}
                sx={textButtonStyle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => deleteDefectLegend()}
                sx={{
                  ...containedButtonStyle,
                  backgroundColor: abyssColors.error[700],
                  '&:hover': { backgroundColor: abyssColors.error[800] },
                }}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      <Stack paddingTop={4}>
        <FormHeader>
          <Typography fontSize="2.2rem" fontWeight={500}>
            Defect Legends
          </Typography>
          {mode === 'ReadOnly' ? (
            <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
              <Tooltip
                title="Edit Defect"
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { tooltipStyles },
                  },
                }}
              >
                <IconButton size="medium" onClick={() => setMode('Edit')}>
                  <Edit
                    fontSize="inherit"
                    sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  setMode('ReadOnly');
                  handleSaveTemplate();
                }}
              >
                Save
              </Button>
              <Button
                variant="contained"
                sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                onClick={() => {
                  setMode('ReadOnly');
                }}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </FormHeader>

        <LineBreak marginBottom={2} />
        <Stack direction="row" width="100%" gap={2}>
          <FieldsList>
            <Stack gap={1}>
              {defectLegends?.map((defectLegend) => (
                <Field
                  selected={defectLegend.id === selectedDefectLegend?.id}
                  key={defectLegend.id}
                  onClick={() => {
                    setSelectedDefectLegend(defectLegend);
                  }}
                >
                  {defectLegend.name}
                </Field>
              ))}
            </Stack>
          </FieldsList>
          {selectedDefectLegend ? (
            <FieldPanel gap={3} direction="row">
              <Stack width={'50%'} gap={1}>
                <TemplateFields
                  label="Name"
                  value={selectedDefectLegend.name}
                  mode={mode}
                  onChange={(value) => {
                    const defect: DefectLegend = { ...selectedDefectLegend, name: value };
                    updateField(defect);
                  }}
                />
                <TemplateFields
                  label="Group Name"
                  value={selectedDefectLegend.groupName}
                  mode="ReadOnly"
                />
                <TemplateFields
                  label="Group Key"
                  value={selectedDefectLegend.groupKey}
                  mode={canManageDefects ? mode : 'ReadOnly'}
                  onChange={(value) => {
                    const defect: DefectLegend = { ...selectedDefectLegend, groupKey: value };
                    updateField(defect);
                  }}
                />
                <TemplateFields
                  label="Color"
                  value={selectedDefectLegend.color}
                  mode={mode}
                  endAdornment="color"
                  onChange={(value) => {
                    const defect: DefectLegend = { ...selectedDefectLegend, color: value };
                    updateField(defect);
                  }}
                />
                <TemplateFields
                  label="Legend Text"
                  value={selectedDefectLegend.legendText}
                  mode={mode}
                  onChange={(value) => {
                    const defect: DefectLegend = { ...selectedDefectLegend, legendText: value };
                    updateField(defect);
                  }}
                />
                <TemplateFields
                  label="Octree Key"
                  value={selectedDefectLegend.octreeKey}
                  mode="ReadOnly"
                />
                <TemplateFields
                  label="Octree Value"
                  value={selectedDefectLegend.octreeValue.toString()}
                  type={TemplateFieldTypeEnum.Numeric}
                  mode="ReadOnly"
                />

                <LineBreak marginY={4} />
                {mode === 'Edit' && canManageDefects && (
                  <Button
                    sx={{
                      ...containedButtonStyle,
                      width: '50%',
                      height: '40px',
                      backgroundColor: abyssColors.error[700],
                      '&:hover': { backgroundColor: abyssColors.error[800] },
                    }}
                    startIcon={<Delete />}
                    variant="contained"
                    size="small"
                    onClick={handleDeleteDefectLegend}
                  >
                    Remove Defect Legend
                  </Button>
                )}
              </Stack>
            </FieldPanel>
          ) : (
            <FieldPanel justifyContent="center" alignItems="center">
              <EmptyStatePlaceholder
                type="poiTable"
                message="Click on any defect type in the left panel to see its information"
              />
            </FieldPanel>
          )}
        </Stack>
      </Stack>
    </>
  );
};
